/**
 * /tenant-selection — Surface Layer (/01)
 *
 * F0.2 Carbon shell (placeholder_only). Lets an actor pick which
 * tenant/workspace context is active. Data comes ONLY from
 * useTenantSelection() (local mock today, usingFallback=true). There is NO
 * backend / BFF / ActionFrame wiring here: the active-tenant claim resolved
 * by the IDL bridge from the verified session token is a later gated step
 * (B2). Every action button is disabled — this view never mutates state and
 * never calls a tool. Visual refinement (iconography, density) is F0.3.
 *
 * Carbon authority: installed @carbon/react v1.108.0 + verified existing /01
 * patterns (mirrors src/routes/ops.tsx empty-state view) — not training data.
 */

import { createFileRoute } from '@tanstack/react-router';
import { Button, Column, Grid, InlineNotification, Tag, Tile } from '@carbon/react';
import { useTenantSelection } from '@lib/persistence/useTenantSelection';
import type { MembershipRole } from '@contracts/tenant-selection.contract';
import '../styles/views.scss';

export const Route = createFileRoute('/tenant-selection')({
  component: TenantSelectionView,
});

// Proven Carbon Tag types only (verified in existing /01 routes).
const ROLE_TAG: Record<MembershipRole, 'purple' | 'blue' | 'teal' | 'cyan' | 'cool-gray'> = {
  owner: 'purple',
  admin: 'blue',
  developer: 'teal',
  user: 'cyan',
  viewer: 'cool-gray',
};

function TenantSelectionView() {
  const { state, usingFallback } = useTenantSelection();

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Select workspace</h1>
        <p className="view__subtitle">
          Choose which tenant / workspace context is active. Depth follows the N-tier model
          (0 = root / owner, 1 = subsidiary, 2+ = sub-tenant).
        </p>
      </header>

      <InlineNotification
        kind={usingFallback ? 'warning' : 'info'}
        lowContrast
        hideCloseButton
        title={usingFallback ? 'Sample — no live session' : 'Live tenant memberships'}
        subtitle={
          usingFallback
            ? 'No signed-in session, so these are a labelled sample (not your real memberships). Sign in to see your live tenant memberships from /api/me.'
            : 'Your real tenant memberships, read live from /api/me (RLS-scoped). Switching the active workspace is a governed action and is not performed from this read-only view.'
        }
      />

      <div className="surface-grid">
        <Grid condensed>
          {state.tenants.map((t) => (
            <Column key={t.tenantId} sm={4} md={4} lg={4}>
              <Tile className="view-tile">
                <div className="view-tile__row">
                  <h2 className="view-tile__value">{t.label}</h2>
                  <Tag type={t.active ? 'teal' : 'cool-gray'}>{t.active ? 'Active' : 'Inactive'}</Tag>
                </div>
                <p className="view-tile__label">Depth {t.depth}</p>
                <div className="view-actions">
                  <Tag type={ROLE_TAG[t.role]}>{t.role}</Tag>
                </div>
                <Button kind="tertiary" disabled>
                  {t.active ? 'Current workspace' : 'Switch to workspace'}
                </Button>
              </Tile>
            </Column>
          ))}
        </Grid>
      </div>
    </div>
  );
}
